import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertTriangle, DollarSign, ArrowRight, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RISK_COLORS = {
  low: '#22C55E',
  moderate: '#EAB308',
  high: '#F97316',
  very_high: '#EF4444',
};

const RISK_TEXTS = {
  low: 'Your system likely still has useful life remaining.',
  moderate: 'Your system may require major repairs within the next few years.',
  high: 'Replacement planning is recommended to avoid unexpected breakdowns.',
  very_high: 'Your system is near the end of its typical lifespan.',
};

const SYSTEM_LABELS = {
  'central-ac': 'Central AC',
  'heat-pump': 'Heat Pump',
  'high-efficiency': 'High-Efficiency System',
  'ductless': 'Ductless Mini-Split',
};

const EstimateResultPage = () => {
  const { id } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const res = await axios.get(`${API}/estimates/${id}`);
        setEstimate(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchEstimate();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <GlobalBanner />
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0EA5E9] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-500">Loading estimate...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen bg-white">
        <GlobalBanner />
        <Navbar />
        <div className="pt-32 pb-20 text-center max-w-md mx-auto px-6">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Estimate Not Found
          </h1>
          <p className="text-slate-500 mb-8">This estimate link may have expired or is invalid.</p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-6 rounded-full">
              Get Your Own Estimate
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const riskColor = RISK_COLORS[estimate.risk_level] || '#22C55E';
  const riskText = RISK_TEXTS[estimate.risk_level] || '';

  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      <section className="pt-28 pb-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="shared-estimate-heading"
          >
            HVAC Estimate Summary
          </h1>
          <p className="text-slate-500">Generated by NOLA HVAC Connect</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 overflow-hidden p-6 md:p-8">

            {/* Price Range */}
            <div className="bg-gradient-to-br from-[#0F172A] to-slate-800 rounded-2xl p-6 text-white mb-6 text-center">
              <p className="text-slate-300 text-sm mb-2">Estimated Replacement Cost</p>
              <p className="text-4xl font-bold mb-2" data-testid="shared-price-range">
                ${estimate.low_estimate.toLocaleString()} - ${estimate.high_estimate.toLocaleString()}
              </p>
              <p className="text-slate-400 text-sm">
                For {estimate.home_sqft.toLocaleString()} sq ft home
              </p>
            </div>

            {/* System Size */}
            <div className="bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 rounded-xl p-4 mb-6 text-center">
              <p className="text-sm text-[#0EA5E9] font-medium mb-1">Estimated System Size</p>
              <p className="text-2xl font-bold text-[#0F172A]" data-testid="shared-tonnage" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {estimate.tonnage} Ton HVAC System
              </p>
            </div>

            {/* Risk Score */}
            <div className="rounded-xl border p-5 mb-6" style={{ borderColor: riskColor + '40' }} data-testid="shared-risk-section">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="w-5 h-5" style={{ color: riskColor }} />
                <h3 className="font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  HVAC System Replacement Risk
                </h3>
              </div>
              <span
                className="inline-block px-3 py-1 rounded-full text-sm font-bold text-white mb-3"
                style={{ backgroundColor: riskColor }}
                data-testid="shared-risk-badge"
              >
                {estimate.risk_label}
              </span>
              <p className="text-slate-600 text-sm">{riskText}</p>
            </div>

            {/* Details */}
            <div className="bg-slate-50 rounded-xl p-5 mb-6">
              <h3 className="font-bold text-[#0F172A] text-sm mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Estimate Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Home Size</span>
                  <span className="font-medium text-[#0F172A]">{estimate.home_sqft.toLocaleString()} sq ft</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">System Type</span>
                  <span className="font-medium text-[#0F172A]">{SYSTEM_LABELS[estimate.system_type] || estimate.system_type}</span>
                </div>
                {estimate.system_age && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">System Age</span>
                    <span className="font-medium text-[#0F172A]">{estimate.system_age} years</span>
                  </div>
                )}
              </div>
            </div>

            {/* Local Context */}
            <div className="bg-slate-50 rounded-xl p-5 mb-6" data-testid="shared-local-context">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-[#0EA5E9]" />
                <h3 className="font-bold text-[#0F172A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Typical SE Louisiana Costs
                </h3>
              </div>
              <div className="space-y-2">
                {[
                  { ton: '3 Ton', range: '$13,000 – $17,500' },
                  { ton: '4 Ton', range: '$15,000 – $20,000' },
                  { ton: '5 Ton', range: '$18,000 – $24,000' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-200 last:border-0">
                    <span className="text-[#0F172A] font-medium">{item.ton}</span>
                    <span className="text-slate-500">{item.range}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a href="/instant-hvac-estimate-new-orleans">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-6 rounded-full w-full text-lg">
                  Get Your Own Estimate
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500 mt-4">
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4 text-[#0EA5E9]" /> Licensed & Insured
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#0EA5E9]" /> Free In-Home Quote
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EstimateResultPage;
